import { useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { costoDeUnidadVenta, DEFAULT_SETTINGS, diasEntre, formatMoney, sumarDias, ZERO_COST, type UnitCost } from '@abastece/shared';
import { ultimoDiaDeOferta, type Oferta, type Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { descargar } from '../lib/archivos.js';
import { buscarEntre, listaPredeterminada, precioDe, unidadesDe } from '../lib/catalogo.js';
import { costoConocido } from '../lib/costos.js';
import { emitirYVer } from '../lib/emitir.js';
import { useHoy } from '../lib/hoy.js';
import { useLectorUsb } from '../lib/lector-usb.js';
import { DIAS_DE_RESULTADOS, VENTANA_OFERTAS, estadoDeOferta, resultadosDeOfertas, sugerirOfertas, textoDeOfertas, type SugerenciaOferta } from '../lib/ofertas.js';
import { filasParaElPersonal, ofertasParaElPersonalEnPdf, tituloParaElPersonal } from '../lib/ofertasParaElPersonal.js';
import { datosNegocio, enlaceWhatsApp } from '../lib/recibo.js';
import { usePuede } from '../lib/sesion.js';
import { fechaCorta } from '../ui/CuentasDeCaja.js';
import { HojaOferta } from '../ui/HojaOferta.js';
import { Ico } from '../ui/Iconos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Confirmar } from '../ui/Preguntar.js';
import { Motivo, PreciosDeOferta, Sugerido, type CambioDeOferta } from '../ui/PreciosDeOferta.js';
import { TarjetaResultado, porSemana } from '../ui/ResultadoDeOferta.js';
import { useNombreProducto } from '../i18n/productos.js';
import { useT, type Clave } from '../i18n/index.js';
import { cantidadCorta, etiquetaPresentacion } from '../i18n/dominio.js';

type Filtro = 'sugeridas' | 'todos' | 'resultados';

/**
 * The suggestions turned down on this device, each with the day it was: in thirty days it is asked
 * again, the stock and its pace having moved since. Kept here and not in the books, as «Paquetes»
 * keeps its own: turning one down records nothing about the shop.
 */
const CLAVE_NO_SUGERIDAS = 'abastece.ofertas.noSugeridas';
const DIAS_SIN_SUGERIR = 30;

function leerNoSugeridas(hoy: string): Map<string, string> {
  try {
    const guardado: unknown = JSON.parse(localStorage.getItem(CLAVE_NO_SUGERIDAS) ?? '{}');
    if (!guardado || typeof guardado !== 'object') return new Map();
    const desde = sumarDias(hoy, -DIAS_SIN_SUGERIR);
    return new Map(Object.entries(guardado as Record<string, unknown>).filter((x): x is [string, string] => typeof x[1] === 'string' && x[1] > desde));
  } catch {
    return new Map();
  }
}

function recordarNoSugeridas(no: ReadonlyMap<string, string>) {
  try {
    localStorage.setItem(CLAVE_NO_SUGERIDAS, JSON.stringify(Object.fromEntries(no)));
  } catch {
    // A device that keeps nothing suggests it again next time: nothing worse.
  }
}

/** What was done to a row on this visit. It stays so until another view is picked. */
type Hecho = { tipo: 'puesta'; sugerencia?: SugerenciaOferta } | { tipo: 'no'; sugerencia: SugerenciaOferta };

/**
 * «Ofertas» (the owner, 2026-11-03: «i want a tool that suggests to me products that haven't been
 * moving so well and i want the tool to recommend me which products to put on sale and for how long,
 * basically a promotion generator»): a tab of «Productos», beside «Paquetes».
 *
 * At the top, what is on sale now or is to be, each with its prices drawn the same way (the list's
 * struck, the offer's, «−30%» or «3×2»: `PreciosDeOferta`), its days and «Terminar», which
 * ends it today (`oferta.terminada`): every till charges the list's price again as soon as it knows.
 * Under it, as on «Paquetes», «Sugeridas»: what `lib/ofertas.ts` finds, expiring batches first, each
 * with why and, in a box of its own (`Sugerido`), each presentation's price now struck beside the
 * suggested one, how much that takes off and for how many days (the owner, 2026-11-03: «i need to see
 * original price -> suggested price»), and «Poner en oferta», which opens the sheet filled in with it
 * (`HojaOferta`), or «No», which puts it aside on this device for thirty days, with «Deshacer». And
 * «Todos», every product on sale, to put one on offer by hand.
 *
 * What each did (2026-11-03, the plan's second part): each running offer says what it has sold and
 * how fast; «Resultados» has a card for each offer of the last ninety days (`TarjetaResultado`).
 * «Mandar por WhatsApp» writes the offers running and to come for the customers (`textoDeOfertas`),
 * and WhatsApp asks whom to.
 *
 * «PDF para el personal» (the owner, 2026-11-03: «print a pdf of all active offers so i can give it to
 * my employees») downloads the offers running today as the till charges them, a page to print, in
 * Spanish and without costs (`ofertasParaElPersonalEnPdf`).
 *
 * Whoever sets the prices: an offer is a price, for some days.
 */
export function Ofertas() {
  const t = useT();
  const nombre = useNombreProducto();
  const hoyEs = useHoy();
  const verCostos = usePuede('ver_costos');
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const precios = useLiveQuery(() => db.precios.toArray(), [], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  const lotes = useLiveQuery(() => db.lotes.toArray(), [], []);
  const compras = useLiveQuery(() => db.compras.toArray(), [], []);
  const ofertas = useLiveQuery(() => db.ofertas.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const [filtro, setFiltro] = useState<Filtro>('sugeridas');
  /**
   * The sales, by the date index: the four weeks the suggestions measure; and, for what the offers
   * did, from four weeks before the first that runs, or, on «Resultados», before the first of the
   * last ninety days. Not a quarter's sales every time the screen opens.
   */
  const desdeVentas = useMemo(() => {
    const desde = [sumarDias(hoyEs, -(VENTANA_OFERTAS - 1))];
    for (const o of ofertas) {
      const estado = estadoDeOferta(o, hoyEs);
      if (estado === 'corre' || (filtro === 'resultados' && estado === 'terminada' && o.desde >= sumarDias(hoyEs, -DIAS_DE_RESULTADOS))) {
        desde.push(sumarDias(o.desde, -VENTANA_OFERTAS));
      }
    }
    return desde.sort()[0]!;
  }, [ofertas, hoyEs, filtro]);
  // With the day they were read from: the reading before a wider one is not taken for it, a moment of
  // an ended offer's results worked out from too few sales.
  const leidas = useLiveQuery(async () => ({ desde: desdeVentas, ventas: await db.ventas.where('fecha').aboveOrEqual(desdeVentas).toArray() }), [desdeVentas]);
  const ventas = leidas?.desde === desdeVentas ? leidas.ventas : undefined;
  /** What was thrown out as expired: what an offer for an expiring batch did not save. */
  const vencidas = useLiveQuery(() => db.salidas.where('motivo').equals('vencido').toArray(), [], []);
  const [consulta, setConsulta] = useState('');
  const [noSugeridas, setNoSugeridas] = useState<ReadonlyMap<string, string>>(() => leerNoSugeridas(hoyEs));
  const [hechos, setHechos] = useState<ReadonlyMap<string, Hecho>>(() => new Map());
  const [abierta, setAbierta] = useState<{ producto: Producto; sugerencia?: SugerenciaOferta } | null>(null);
  const [porTerminar, setPorTerminar] = useState<Oferta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const caja = useRef<HTMLInputElement>(null);
  useLectorUsb(caja, setConsulta);

  const lista = listaPredeterminada(listas.filter((l) => l.activa));
  const porId = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);
  const activos = useMemo(() => productos.filter((p) => p.activo), [productos]);

  const sugerencias = useMemo(
    () =>
      ventas && lista
        ? sugerirOfertas({
            productos,
            unidades,
            precios,
            listaId: lista.id,
            existencias,
            lotes,
            ventas,
            compras,
            ofertas,
            hoy: hoyEs,
            alertaDias: config.vencimientoAlertaDias,
            noSugeridos: new Set(noSugeridas.keys()),
          })
        : [],
    [ventas, lista, productos, unidades, precios, existencias, lotes, compras, ofertas, hoyEs, config.vencimientoAlertaDias, noSugeridas],
  );
  const sugerenciaDe = useMemo(() => new Map(sugerencias.map((s) => [s.productoId, s])), [sugerencias]);

  /** On sale now or to be, on the shop's list: the soonest to end first. */
  const vigentes = useMemo(
    () =>
      ofertas
        .filter((o) => o.listaId === lista?.id && estadoDeOferta(o, hoyEs) !== 'terminada')
        .sort((a, b) => ultimoDiaDeOferta(a).localeCompare(ultimoDiaDeOferta(b)) || a.desde.localeCompare(b.desde)),
    [ofertas, lista, hoyEs],
  );
  const enOfertaHasta = useMemo(() => new Map(vigentes.map((o) => [o.productoId, o])), [vigentes]);

  /** What each did: those that run, and the ended ones of the last ninety days. */
  const resultados = useMemo(
    () =>
      ventas && lista
        ? resultadosDeOfertas({
            ofertas: ofertas.filter(
              (o) => o.listaId === lista.id && (estadoDeOferta(o, hoyEs) === 'corre' || o.desde >= sumarDias(hoyEs, -DIAS_DE_RESULTADOS)),
            ),
            ventas,
            salidas: vencidas,
            lotes,
            existencias,
            hoy: hoyEs,
          })
        : [],
    [ventas, lista, ofertas, vencidas, lotes, existencias, hoyEs],
  );
  const resultadoDe = useMemo(() => new Map(resultados.map((r) => [r.oferta.id, r])), [resultados]);
  /** The results the search leaves, by their product's name or code. */
  const resultadosVistos = useMemo(() => {
    if (!consulta.trim()) return resultados;
    const encontrados = new Set(buscarEntre(productos, consulta, Infinity, nombre, unidades).map((p) => p.id));
    return resultados.filter((r) => encontrados.has(r.oferta.productoId));
  }, [resultados, consulta, productos, nombre, unidades]);

  /** The offers running and to come, as the customers will read them over WhatsApp; null with none. */
  const paraClientes = lista
    ? textoDeOfertas({
        ofertas,
        productos,
        unidades,
        precios,
        listaId: lista.id,
        negocio: datosNegocio(config),
        hoy: hoyEs,
        presentacion: (u, unidadBase) => etiquetaPresentacion(u, unidadBase),
      })
    : null;

  /** The offers running today as the till charges them, for the staff's page; none with none running. */
  const paraElPersonal = useMemo(
    () => (lista ? filasParaElPersonal({ ofertas, productos, unidades, precios, listaId: lista.id, hoy: hoyEs }) : []),
    [lista, ofertas, productos, unidades, precios, hoyEs],
  );

  function descargarParaElPersonal() {
    const pdf = ofertasParaElPersonalEnPdf(paraElPersonal, { negocio: config.negocioNombre, hoy: hoyEs });
    descargar(new File([pdf.bytes(tituloParaElPersonal(config.negocioNombre))], t('ofertas.personalArchivo', { fecha: hoyEs }), { type: 'application/pdf' }));
  }

  const filas = useMemo(() => {
    if (filtro === 'todos') return buscarEntre(activos, consulta, Infinity, nombre, unidades);
    const deLaVista = activos.filter((p) => sugerenciaDe.has(p.id) || hechos.has(p.id));
    const encontrados = buscarEntre(deLaVista, consulta, Infinity, nombre, unidades);
    // In the suggestions' order; a row done keeps the place its suggestion gave it.
    const orden = new Map(sugerencias.map((s, i) => [s.productoId, i]));
    const lugar = (p: Producto) => orden.get(p.id) ?? orden.get(hechos.get(p.id)?.sugerencia?.productoId ?? '') ?? Infinity;
    return [...encontrados].sort((a, b) => lugar(a) - lugar(b));
  }, [filtro, activos, sugerenciaDe, hechos, consulta, nombre, unidades, sugerencias]);

  const filtros: readonly { vista: Filtro; etiqueta: Clave; cuantos?: number }[] = [
    { vista: 'sugeridas', etiqueta: 'ofertas.sugeridas', cuantos: sugerencias.length },
    { vista: 'todos', etiqueta: 'ofertas.todos' },
    { vista: 'resultados', etiqueta: 'ofertas.resultados', cuantos: resultados.length },
  ];

  /** Why a product is suggested, in a line: what expires and when, or how long what there is lasts. */
  function porque(p: Producto, s: SugerenciaOferta): string {
    const partes: string[] = [];
    if (s.vence) {
      partes.push(t(s.vence.dias === 0 ? 'ofertas.porqueVenceHoy' : s.vence.dias === 1 ? 'ofertas.porqueVenceManana' : 'ofertas.porqueVence', { cantidad: cantidadCorta(s.vence.sobra, p.unidadBase), fecha: fechaCorta(s.vence.fecha), dias: s.vence.dias }));
    } else if (s.semanas === null) {
      partes.push(t('ofertas.porqueParado', { cantidad: cantidadCorta(s.cantidad, p.unidadBase), dias: VENTANA_OFERTAS }));
    } else {
      partes.push(t('ofertas.porqueLento', { cantidad: cantidadCorta(s.cantidad, p.unidadBase), semanas: Math.round(s.semanas) }));
    }
    if (verCostos) partes.push(t(s.vence ? 'ofertas.seVencen' : 'ofertas.parados', { monto: formatMoney(s.enJuego) }));
    return partes.join(' · ');
  }

  /**
   * An offer's presentations as `PreciosDeOferta` draws them: the list's price now beside each, and
   * for whoever sees costs what one costs now (`costoConocido`, as the sheet and «Precios» work it out).
   */
  function cambiosDe(o: Oferta, p: Producto | undefined): CambioDeOferta[] {
    const costoBase: UnitCost = verCostos ? costoConocido(o.productoId, 'tienda', existencias, compras) : ZERO_COST;
    return o.precios.flatMap((x) => {
      const u = unidades.find((y) => y.id === x.unidadVentaId);
      if (!u) return [];
      const regular = precioDe(precios, u.id, o.listaId);
      const costo = costoBase > 0 ? costoDeUnidadVenta(costoBase, u.factor) : null;
      return [
        {
          id: u.id,
          presentacion: p ? etiquetaPresentacion(u, p.unidadBase) : u.nombre,
          regular: regular !== null && regular > 0 ? regular : null,
          oferta: 'lleva' in x ? { lleva: { cantidad: x.lleva, paga: x.paga } } : { precio: x.precio },
          costo: costo !== null && costo > 0 ? costo : null,
        },
      ];
    });
  }

  function verOtra(v: Filtro) {
    setFiltro(v);
    setHechos(new Map());
  }

  function hecho(productoId: string, h: Hecho | null) {
    setHechos((antes) => {
      const despues = new Map(antes);
      if (h) despues.set(productoId, h);
      else despues.delete(productoId);
      return despues;
    });
  }

  /** «No»: aside on this device for thirty days, the row kept with its «Deshacer». */
  function noSugerir(s: SugerenciaOferta, poner: boolean) {
    const no = new Map(noSugeridas);
    if (poner) no.set(s.productoId, hoyEs);
    else no.delete(s.productoId);
    setNoSugeridas(no);
    recordarNoSugeridas(no);
    hecho(s.productoId, poner ? { tipo: 'no', sugerencia: s } : null);
  }

  async function terminar(o: Oferta) {
    setError(null);
    try {
      const { rechazo } = await emitirYVer('oferta.terminada', { ofertaId: o.id, fecha: hoyEs });
      if (rechazo) setError(rechazo);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('ofertas.noSePudo'));
    }
    setPorTerminar(null);
  }

  if (activos.length === 0) {
    return (
      <Pagina titulo={t('ofertas.titulo')}>
        <Vacio mensaje={t('ofertas.nadaQueMostrar')} icono="productos" />
      </Pagina>
    );
  }

  const productoPorTerminar = porTerminar ? porId.get(porTerminar.productoId) : undefined;

  return (
    <Pagina titulo={t('ofertas.titulo')} subtitulo={t('ofertas.subtitulo')}>
      <p className="mb-4 rounded-xl bg-slate-900 px-4 py-3 text-xs text-slate-400">{t('ofertas.explicacion', { dias: VENTANA_OFERTAS })}</p>
      {error && <p className="mb-4 text-sm text-rose-400">{error}</p>}

      {vigentes.length > 0 && (
        <section className="mb-6" data-ofertas-vigentes>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-medium text-slate-300">{t('ofertas.enOferta', { n: vigentes.length })}</h2>
            <div className="flex flex-wrap gap-2">
              {paraElPersonal.length > 0 && (
                <button
                  type="button"
                  className="btn-secundario h-10 min-h-0 gap-1.5 px-3 text-sm"
                  onClick={descargarParaElPersonal}
                  title={t('ofertas.personalExplica')}
                  data-ofertas-personal
                >
                  <Ico icono="descargar" className="size-4 shrink-0" />
                  {t('ofertas.personal')}
                </button>
              )}
              {paraClientes && (
                <a
                  className="btn-secundario h-10 min-h-0 gap-1.5 px-3 text-sm"
                  href={enlaceWhatsApp(paraClientes)}
                  target="_blank"
                  rel="noreferrer"
                  title={t('ofertas.whatsappExplica')}
                  data-ofertas-whatsapp
                >
                  <Ico icono="whatsapp" className="size-4 shrink-0" />
                  {t('ofertas.whatsapp')}
                </a>
              )}
            </div>
          </div>
          <ul className="flex flex-col gap-2">
            {vigentes.map((o) => {
              const p = porId.get(o.productoId);
              const estado = estadoDeOferta(o, hoyEs);
              const ultimo = ultimoDiaDeOferta(o);
              const r = resultadoDe.get(o.id);
              return (
                <li key={o.id} className="tarjeta flex flex-wrap items-center gap-3 p-3" data-oferta-vigente={o.productoId} data-oferta-estado={estado}>
                  {p && <Miniatura producto={p} tamano="lg" />}
                  <div className="min-w-0 flex-1 basis-40">
                    <p className="truncate font-medium">{p ? nombre(p) : t('pos.producto')}</p>
                    <p className="mt-0.5 text-xs text-slate-400" data-oferta-dias>
                      {estado === 'programada'
                        ? t('ofertas.empieza', { desde: fechaCorta(o.desde), hasta: fechaCorta(ultimo) })
                        : t(ultimo === hoyEs ? 'ofertas.terminaHoy' : 'ofertas.hasta', { hasta: fechaCorta(ultimo), n: diasEntre(hoyEs, ultimo) + 1 })}
                      {' · '}
                      {t(`ofertas.motivo.${o.motivo}` as Clave)}
                    </p>
                    {/* What it has sold so far, and how fast against before it. */}
                    {r && p && (
                      <p className="tabular mt-0.5 text-xs text-slate-300" data-oferta-van>
                        {r.dias === 1
                          ? t('ofertas.vanHoy', { n: cantidadCorta(r.vendidas, p.unidadBase) })
                          : r.porSemanaAntes > 0
                            ? t('ofertas.van', {
                                n: cantidadCorta(r.vendidas, p.unidadBase),
                                dias: r.dias,
                                semana: porSemana(r.porSemanaDurante, p.unidadBase),
                                antes: porSemana(r.porSemanaAntes, p.unidadBase),
                              })
                            : t('ofertas.vanNuevo', { n: cantidadCorta(r.vendidas, p.unidadBase), dias: r.dias, semana: porSemana(r.porSemanaDurante, p.unidadBase) })}
                      </p>
                    )}
                  </div>
                  {/* Its prices, the list's struck: under the name on a phone, under «Terminar» on a wider screen. */}
                  <div className="w-full rounded-xl bg-teal-500/[0.06] p-3 ring-1 ring-teal-500/15 ring-inset sm:order-last" data-oferta-precios>
                    <PreciosDeOferta cambios={cambiosDe(o, p)} minimoBp={config.precioMargenMinimoBp} />
                  </div>
                  <button
                    type="button"
                    className="btn-secundario h-11 min-h-0 w-full gap-1.5 px-4 text-sm whitespace-nowrap sm:w-auto"
                    onClick={() => setPorTerminar(o)}
                    aria-label={t('ofertas.terminarDe', { producto: p ? nombre(p) : '' })}
                    data-oferta-terminar={o.productoId}
                  >
                    <Ico icono="cancelar" className="size-4" />
                    {t('ofertas.terminar')}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <input
          ref={caja}
          className="campo min-w-0 flex-1"
          placeholder={t('ofertas.buscar')}
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          autoComplete="off"
          inputMode="search"
        />
        <div className="flex shrink-0 gap-1 rounded-xl bg-slate-800 p-1">
          {filtros.map(({ vista, etiqueta, cuantos }) => (
            <button
              key={vista}
              type="button"
              onClick={() => verOtra(vista)}
              aria-pressed={filtro === vista}
              data-ofertas-filtro={vista}
              data-sin-icono="pestana"
              className={`min-h-10 flex-auto rounded-lg px-2.5 py-1 text-sm leading-tight transition max-[359px]:px-2 ${
                filtro === vista ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              <span className="whitespace-nowrap">{t(etiqueta)}</span>
              {cuantos !== undefined && (
                <>
                  {' '}
                  <span className="tabular whitespace-nowrap">({cuantos})</span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* The sales are read a moment after the products: nothing is said of them until they are. */}
      {ventas === undefined ? null : filtro === 'resultados' ? (
        <>
          <p className="mb-3 text-xs text-slate-400">{t('ofertas.resultadosExplica', { dias: DIAS_DE_RESULTADOS })}</p>
          {resultadosVistos.length === 0 ? (
            <Vacio mensaje={t(consulta.trim() ? 'ofertas.ningunoCoincide' : 'ofertas.sinResultados', { dias: DIAS_DE_RESULTADOS })} icono="analisis" />
          ) : (
            <ul className="flex flex-col gap-2" data-ofertas-resultados>
              {resultadosVistos.map((r) => {
                const primera = r.oferta.precios[0];
                return (
                  <TarjetaResultado
                    key={r.oferta.id}
                    resultado={r}
                    producto={porId.get(r.oferta.productoId)}
                    regular={primera ? precioDe(precios, primera.unidadVentaId, r.oferta.listaId) : null}
                    verCostos={verCostos}
                  />
                );
              })}
            </ul>
          )}
        </>
      ) : filas.length === 0 ? (
        <Vacio mensaje={t(consulta.trim() ? 'ofertas.ningunoCoincide' : filtro === 'sugeridas' ? 'ofertas.nadaQueSugerir' : 'ofertas.nadaQueMostrar')} icono="productos" />
      ) : (
        <ul className="flex flex-col gap-2">
          {filas.map((p) => {
            const h = hechos.get(p.id);
            const s = sugerenciaDe.get(p.id);
            const yaEn = enOfertaHasta.get(p.id);
            const tienePrecio = lista !== undefined && unidadesDe(unidades, p.id).some((u) => precios.some((x) => x.unidadVentaId === u.id && x.listaId === lista.id && x.precio > 0));
            return (
              <li key={p.id} className="tarjeta flex flex-wrap items-center gap-3 p-3" data-ofertas-producto={p.id} data-ofertas-estado={h?.tipo ?? (yaEn ? 'en-oferta' : s ? 'sugerida' : 'nada')}>
                <Miniatura producto={p} tamano="lg" />
                <div className="min-w-0 flex-1 basis-40">
                  <p className="truncate font-medium">{nombre(p)}</p>
                  {h?.tipo === 'puesta' ? (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-teal-300" data-ofertas-hecho>
                      <Ico icono="listo" className="size-3.5 shrink-0" />
                      <span className="min-w-0">{t('ofertas.puesta')}</span>
                    </p>
                  ) : h?.tipo === 'no' ? (
                    <p className="mt-0.5 text-xs text-slate-400" data-ofertas-hecho>
                      {t('ofertas.yaNo', { dias: DIAS_SIN_SUGERIR })}
                    </p>
                  ) : yaEn ? (
                    <p className="mt-0.5 text-xs text-teal-300">{t('ofertas.yaEnOferta', { hasta: fechaCorta(ultimoDiaDeOferta(yaEn)) })}</p>
                  ) : s ? (
                    <p className="mt-1 text-xs text-slate-400">
                      <Motivo motivo={s.motivo} />
                      <span data-ofertas-porque>{porque(p, s)}</span>
                    </p>
                  ) : !tienePrecio ? (
                    <p className="mt-0.5 text-xs text-slate-500">{t('ofertas.sinPrecio')}</p>
                  ) : null}
                </div>
                {/* What is suggested: under the name on a phone, under the buttons on a wider screen. */}
                {s && !h && !yaEn && (
                  <div className="w-full sm:order-last">
                    <Sugerido sugerencia={s} unidadBase={p.unidadBase} verCostos={verCostos} minimoBp={config.precioMargenMinimoBp}>
                      {s.bajoCosto && verCostos && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300" data-ofertas-bajo-costo>
                          <Ico icono="aviso" className="size-3.5 shrink-0" />
                          {t('ofertas.bajoCostoAviso')}
                        </p>
                      )}
                    </Sugerido>
                  </div>
                )}
                <div className="flex w-full gap-2 sm:w-auto">
                  {h?.tipo === 'no' ? (
                    <button
                      type="button"
                      className="btn-secundario h-11 min-h-0 flex-1 gap-1.5 px-4 text-sm whitespace-nowrap sm:flex-none"
                      onClick={() => noSugerir(h.sugerencia, false)}
                      data-ofertas-deshacer={p.id}
                    >
                      <Ico icono="devolver" className="size-4" />
                      {t('ofertas.deshacer')}
                    </button>
                  ) : h?.tipo === 'puesta' || yaEn || !tienePrecio || !lista ? null : (
                    <>
                      <button
                        type="button"
                        className="btn-secundario h-11 min-h-0 flex-1 gap-1.5 px-4 text-sm whitespace-nowrap sm:flex-none"
                        onClick={() => setAbierta({ producto: p, ...(s ? { sugerencia: s } : {}) })}
                        aria-label={t('ofertas.ponerDe', { producto: nombre(p) })}
                        data-ofertas-poner={p.id}
                      >
                        <Ico icono="precios" className="size-4" />
                        {t('ofertas.poner')}
                      </button>
                      {s && filtro === 'sugeridas' && (
                        <button
                          type="button"
                          className="btn-secundario h-11 min-h-0 gap-1.5 px-4 text-sm whitespace-nowrap"
                          onClick={() => noSugerir(s, true)}
                          aria-label={t('ofertas.noDe', { producto: nombre(p) })}
                          title={t('ofertas.noExplicado', { dias: DIAS_SIN_SUGERIR })}
                          data-ofertas-no={p.id}
                        >
                          <Ico icono="cancelar" className="size-4" />
                          {t('comun.no')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {abierta && lista && (
        <HojaOferta
          producto={abierta.producto}
          listaId={lista.id}
          sugerencia={abierta.sugerencia}
          porque={abierta.sugerencia ? porque(abierta.producto, abierta.sugerencia) : undefined}
          hoy={hoyEs}
          onPuesta={() => hecho(abierta.producto.id, { tipo: 'puesta', ...(abierta.sugerencia ? { sugerencia: abierta.sugerencia } : {}) })}
          onCerrar={() => setAbierta(null)}
        />
      )}

      {porTerminar && (
        <Confirmar
          titulo={t('ofertas.terminarTitulo')}
          mensaje={t(estadoDeOferta(porTerminar, hoyEs) === 'programada' ? 'ofertas.terminarProgramada' : 'ofertas.terminarMensaje', {
            producto: productoPorTerminar ? nombre(productoPorTerminar) : '',
          })}
          confirmar={t('ofertas.terminar')}
          icono="cancelar"
          peligro
          onCerrar={() => setPorTerminar(null)}
          onConfirmar={() => void terminar(porTerminar)}
        />
      )}
    </Pagina>
  );
}
